<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import type { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";
import { computed } from "vue";
import type { TwoColumnsWithScreenshotProps } from "./TwoColumnsWithScreenshotProps";
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import Image from "@/views/blocks/shared/section/media/image/Image.vue";
import PrimaryButton from "@/views/blocks/shared/section/buttons/primary-button/PrimaryButton.vue";

const props = defineProps<{
    block: PageBlock<TwoColumnsWithScreenshotProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section :id="block.webId" :backgroundColor="styleValues.background">
        <FullSpacing>
            <div class="max-w-7xl mx-auto">
                <div class="max-w-2xl lg:max-w-none mx-auto">
                    <EditableElement
                        v-if="!dataValues.tag.isHidden"
                        :blockId="block.id"
                        fieldName="tag"
                    >
                        <Heading
                            :tag="dataValues.tag.data.tag"
                            :text="dataValues.tag.data.text"
                            :textColor="styleValues.brand"
                            class="text-base/7 mb-2"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :blockId="block.id"
                        fieldName="title"
                    >
                        <Heading
                            :text="dataValues.title.data.text"
                            :tag="dataValues.title.data.tag"
                            :textColor="styleValues.text"
                            class="text-4xl lg:text-5xl mb-10"
                        />
                    </EditableElement>

                    <div class="columns-1 lg:columns-2 gap-8">
                        <EditableElement
                            v-for="paragraph in dataValues.paragraphs.data.items"
                            :key="paragraph.id"
                            :blockId="block.id"
                            :fieldName="`paragraphs_${paragraph.id}`"
                            :hideable="false"
                        >
                            <Paragraph
                                :text="paragraph.text"
                                :textColor="styleValues.text"
                                class="text-base/7 mb-8 opacity-70"
                            />
                        </EditableElement>
                    </div>

                    <EditableElement
                        v-if="!dataValues.primaryButton.isHidden"
                        :blockId="block.id"
                        fieldName="primaryButton"
                        class="mt-8"
                    >
                        <PrimaryButton
                            :label="dataValues.primaryButton.data.label"
                            :href="dataValues.primaryButton.data.href"
                            :backgroundColor="styleValues.brand"
                            :textColor="styleValues.background"
                            class="mt-4 text-sm px-3.5 py-2.5 rounded-md"
                        />
                    </EditableElement>
                </div>

                <EditableElement
                    v-if="!dataValues.screenshot.isHidden"
                    :blockId="block.id"
                    fieldName="screenshot"
                >
                    <div class="relative mt-20">
                        <Image
                            :src="dataValues.screenshot.data.src"
                            :alt="dataValues.screenshot.data.alt"
                            class="w-full rounded-xl object-cover"
                        />
                        <div
                            class="absolute -bottom-1 left-0 right-0 h-24 sm:h-32 bg-gradient-to-t"
                            :style="{
                                backgroundImage: `linear-gradient(to top, ${styleValues.background} 0%, transparent 100%)`,
                            }"
                        ></div>
                    </div>
                </EditableElement>
            </div>
        </FullSpacing>
    </Section>
</template>
